import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps } from 'src/components/Box';
import { Meta } from 'src/components/Meta';
import { MobileAppHeader } from 'src/components/MobileAppHeader';
import { SafeArea } from 'src/components/SafeArea';
import { useOptionalSession } from 'src/contexts/SessionContext';

interface FaqWebviewShellProps {
  children: React.ReactNode;
  contentSx?: BoxProps['sx'];
}

interface FaqCenteredStateProps {
  children: React.ReactNode;
  sx?: BoxProps['sx'];
}

export function FaqWebviewShell({ children, contentSx }: FaqWebviewShellProps) {
  const { session } = useOptionalSession();
  const { t } = useTranslation('translation', { keyPrefix: 'appWebview.faq' });
  const [headerHeight, setHeaderHeight] = useState(120);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const stickyHeader = stickyHeaderRef.current;
    if (!stickyHeader) {
      return;
    }

    const updateHeaderHeight = () => {
      const nextHeight = Math.ceil(stickyHeader.getBoundingClientRect().height);

      if (nextHeight > 0) {
        setHeaderHeight(nextHeight);
      }
    };

    updateHeaderHeight();

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeaderHeight);
      resizeObserver.observe(stickyHeader);
    }

    window.addEventListener('orientationchange', updateHeaderHeight);
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('orientationchange', updateHeaderHeight);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  return (
    <Box
      sx={{
        '--faqAnchorOffset': `${headerHeight + 12}px`,
        bgcolor: 'background.default',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
      }}
    >
      <Meta title={t('title', { defaultValue: 'FAQs' })} brand={session?.brand} />
      <Box
        ref={stickyHeaderRef}
        sx={{
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <MobileAppHeader
          title={t('title', { defaultValue: 'FAQs' })}
          row={<Box sx={{ bgcolor: 'primary.solid', height: '4px' }} />}
        />
      </Box>

      <SafeArea
        bottom
        sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
      >
        <Box
          sx={[
            {
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
            },
            ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
          ]}
        >
          {children}
        </Box>
      </SafeArea>
    </Box>
  );
}

export function FaqCenteredState({ children, sx }: FaqCenteredStateProps) {
  return (
    <Box
      sx={[
        {
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          minHeight: '40dvh',
          p: 3,
          textAlign: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}
